import { optAppConfig } from '@/utils/config';
import { SpansContext } from '@/components/context/SpansContext';
import { StepProgress } from '@/models/StepProgress';
import { ThreadId } from '@/models/Thread';
import { WidgetType } from '@/models/PanelWidgets';

import { useContext } from 'react';

export const SEND_MESSAGE_TRACE_KEY = 'send-message';
export const SEND_MESSAGE_STEP_PROGRESS_TRACE_KEY =
  'send-msg-processing-step-progress';
export const SEND_MESSAGE_BOT_RESPONSE_TRACE_KEY = 'send-msg-bot-response';
export const SEND_MESSAGE_WIDGET_CREATED_TRACE_KEY = 'send-msg-widget-created';

export const createThreadTraceKey = (
  prefix: string,
  threadId: ThreadId,
): string => {
  return `${prefix}__${threadId}`;
};

export function useTraces() {
  const { endSpan, getOrCreateSpan, getSpan } = useContext(SpansContext);

  const traceProcessingStepProgress = (stepProgress: StepProgress): void => {
    const threadId = stepProgress.threadId;
    const spanKey = createThreadTraceKey(
      SEND_MESSAGE_STEP_PROGRESS_TRACE_KEY,
      threadId,
    );
    const span = getSpan(spanKey);
    if (span) {
      endSpan(spanKey);
    }
  };

  const traceBotResponseMessage = ({
    threadId,
    senderId,
  }: {
    threadId: ThreadId;
    senderId: string;
  }): void => {
    const spanKey = createThreadTraceKey(
      SEND_MESSAGE_BOT_RESPONSE_TRACE_KEY,
      threadId,
    );
    const span = getSpan(spanKey);
    const botActorId = optAppConfig((_) => _.bot.actor_id);
    const isFromBot = senderId === botActorId;

    if (span && isFromBot) {
      endSpan(spanKey);
    }
  };

  const traceWidgetCreated = ({
    threadId,
    widgetId,
    widgetType,
  }: {
    threadId: ThreadId;
    widgetId: string;
    widgetType: WidgetType;
  }): void => {
    const spanKey = createThreadTraceKey(
      SEND_MESSAGE_WIDGET_CREATED_TRACE_KEY,
      threadId,
    );
    const span = getSpan(spanKey);
    span?.setAttribute('widget_id', widgetId);
    span?.setAttribute('widget_type', widgetType);

    if (span) {
      endSpan(spanKey);
    }
  };

  const initializeSendMessageTraces = (threadId: ThreadId): void => {
    // track time until the first step progress is received
    getOrCreateSpan({
      key: createThreadTraceKey(SEND_MESSAGE_STEP_PROGRESS_TRACE_KEY, threadId),
      spanName: SEND_MESSAGE_STEP_PROGRESS_TRACE_KEY,
    });

    // track time until the bot response is received
    getOrCreateSpan({
      key: createThreadTraceKey(SEND_MESSAGE_BOT_RESPONSE_TRACE_KEY, threadId),
      spanName: SEND_MESSAGE_BOT_RESPONSE_TRACE_KEY,
    });

    // track time until a widget is created
    getOrCreateSpan({
      key: createThreadTraceKey(
        SEND_MESSAGE_WIDGET_CREATED_TRACE_KEY,
        threadId,
      ),
      spanName: SEND_MESSAGE_WIDGET_CREATED_TRACE_KEY,
    });
  };

  return {
    initializeSendMessageTraces,
    traceBotResponseMessage,
    traceProcessingStepProgress,
    traceWidgetCreated,
  };
}
